// 週報検索のURL用: 複数値パラメータをカンマ区切り1本にまとめてURL長を抑える。
//   書き出し: teams=81&teams=82  -> teams=81,82
//   読み込み: teams=81,82 も teams=81&teams=82（従来形式）も同じ複数値として扱う
// API送信用パラメータ（buildWeeklySearchParams）はBE契約のため対象外。

export const WEEKLY_SEARCH_LIST_URL_KEYS = [
  'report_types',
  'teams',
  'departments',
  'create_by',
  'create_by_user_ids',
  'legacy_create_by',
] as const;

const LIST_KEY_SET = new Set<string>(WEEKLY_SEARCH_LIST_URL_KEYS);

// カンマ区切り形式のURLにだけ付ける形式マーカー。
// マーカーが無いURL（この対応以前に発行されたURL）は従来形式として一切分割せず読む。
// これにより、値にカンマを含む旧URLも誤って分割されない。
export const WEEKLY_SEARCH_URL_FORMAT_PARAM = 'url_fmt';
const WEEKLY_SEARCH_URL_FORMAT_VERSION = '2';

// 値に含まれる「\」「,」は「\\」「\,」にエスケープする
const escapeListItem = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/,/g, '\\,');

const splitListValue = (raw: string): string[] => {
  if (!raw.includes(',') && !raw.includes('\\')) return [raw];

  const items: string[] = [];
  let current = '';
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    if (ch === '\\' && i + 1 < raw.length) {
      current += raw[i + 1];
      i += 1;
    } else if (ch === ',') {
      items.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  items.push(current);
  return items;
};

/** 複数値キーを1本のカンマ区切りにまとめる（URL書き込み用） */
export function compressWeeklySearchListParams(
  params: URLSearchParams
): URLSearchParams {
  const grouped = new Map<string, string[]>();
  params.forEach((value, key) => {
    if (!LIST_KEY_SET.has(key)) return;
    const list = grouped.get(key) ?? [];
    list.push(value);
    grouped.set(key, list);
  });

  const out = new URLSearchParams();
  const written = new Set<string>();
  params.forEach((value, key) => {
    if (!LIST_KEY_SET.has(key)) {
      out.append(key, value);
      return;
    }
    if (written.has(key)) return;
    written.add(key);
    out.append(key, (grouped.get(key) ?? []).map(escapeListItem).join(','));
  });
  if (grouped.size > 0) {
    out.set(WEEKLY_SEARCH_URL_FORMAT_PARAM, WEEKLY_SEARCH_URL_FORMAT_VERSION);
  }
  return out;
}

/**
 * カンマ区切りを従来の複数パラメータへ展開する（URL読み込み用）。
 * 形式マーカーが無い（= 従来形式の）URLは、同名パラメータ複数のまま値を一切変更しない。
 */
export function expandWeeklySearchListParams(
  params: URLSearchParams
): URLSearchParams {
  const isCompressed =
    params.get(WEEKLY_SEARCH_URL_FORMAT_PARAM) ===
    WEEKLY_SEARCH_URL_FORMAT_VERSION;

  const out = new URLSearchParams();
  params.forEach((value, key) => {
    if (key === WEEKLY_SEARCH_URL_FORMAT_PARAM) return;
    if (!isCompressed || !LIST_KEY_SET.has(key)) {
      out.append(key, value);
      return;
    }
    splitListValue(value).forEach((item) => out.append(key, item));
  });
  return out;
}
